// TelcoTrack — BUDOVA parallel milestone engine (Sprint 1 foundation).
//
// Aggregate `status` stays Nepřipojený until all required milestones for the building type
// are complete; linear phases (Zafouknutý → Svařený on Připojená) gate on this engine.
// Odbočené is engine-only — no Excel pseudo-phase in Sprint 1.

import type { BudovaBuildingType } from './budova-building-type.constants';
import { isBudovaBuildingType } from './budova-building-type.constants';
import { isMsoHp } from '../../utils/budova-hp-display.util';
import { getPhaseName, normalizeObjectTypeForLookup, PHASE_COLORS, PHASE_CODE } from './phase-codes';
import { getPhaseByCode, hasReachedWorkflowMilestone, isSamePhase } from './workflow-model';
import { parseWorkflowVariant } from './variants';
import { resolveBranchParentBudovaId } from '../vykop-variant.constants';
import {
  type BudovaBranchOriginKind,
} from './budova-branch-origin.constants';

export { BUDOVA_BUILDING_TYPES, type BudovaBuildingType } from './budova-building-type.constants';

/** Připojená post-milestone workflow phase (`BUDOVA.2.PHASE.1` per Výkresy). */
export const BUDOVA_PRIPOJENA_POST_MILESTONE_PHASE_CODE = 'BUDOVA.2.PHASE.1';

/** Zafouknutý — workflow-specific codes (PHASE_CODE.BUDOVA_ZAFOUKNUTO is BUDOVA.2 rep only). */
export const BUDOVA_PRIPOJENA_ZAFOUKNUTO_PHASE_CODE = 'BUDOVA.2.PHASE.2';
export const BUDOVA_BABOVKA_ZAFOUKNUTO_PHASE_CODE = 'BUDOVA.3.PHASE.2';

/**
 * True when `pripojovany_objekt_spec.digitalni_cesta` (count) is a positive integer.
 * Null / 0 / missing → no digitální cesta → Zafouknutý is terminal (no Svařený).
 */
export function budovaHasDigitalniCesta(
  digitalniCesta: number | null | undefined,
): boolean {
  const n = Math.trunc(Number(digitalniCesta));
  return Number.isFinite(n) && n > 0;
}

/** Připojená linear Svařený only when at least one digitální cesta exists. */
export function budovaRequiresSvarPhase(
  digitalniCesta: number | null | undefined,
): boolean {
  return budovaHasDigitalniCesta(digitalniCesta);
}

/**
 * Map/status color override for Připojená when digitální cesta is absent:
 * Připojené plasty → orange; Zafouknutý → green (done). With digitální cesta → null (Excel colors).
 */
export function resolveBudovaPripojenaPhaseColorOverride(
  phaseCode: string | null | undefined,
  digitalniCesta: number | null | undefined,
): string | null {
  if (budovaHasDigitalniCesta(digitalniCesta)) return null;
  const code = (phaseCode ?? '').trim();
  if (!code) return null;
  // Only Připojená (BUDOVA.2) — Bábovka already ends green at záfuk.
  if (!code.startsWith('BUDOVA.2.')) return null;
  if (isSamePhase(code, PHASE_CODE.BUDOVA_PRIPOJENE_PLASTY)) return PHASE_COLORS.ORANGE;
  if (isSamePhase(code, PHASE_CODE.BUDOVA_ZAFOUKNUTO)) return PHASE_COLORS.GREEN;
  return null;
}

/**
 * Budova je v poslední lineární fázi (hotovo):
 * - workflow `isFinal` (Svařený, Bábovka Zafouknutý, Zrušený, …)
 * - Připojená Zafouknutý bez digitální cesty (svár se nevyžaduje)
 *
 * `digitalniCesta === undefined` = neznámé → jen workflow `isFinal` (ne zkracovat na záfuk).
 * Explicitní `null` / `0` = bez DC → Zafouknutý je terminální.
 */
export function isBudovaLastPhase(
  status: string | null | undefined,
  digitalniCesta?: number | null,
): boolean {
  const code = (status ?? '').trim();
  if (!code) return false;
  const phase = getPhaseByCode('BUDOVA', code);
  if (phase?.isFinal) return true;
  if (
    digitalniCesta !== undefined
    && isSamePhase(code, PHASE_CODE.BUDOVA_ZAFOUKNUTO)
    && code.startsWith('BUDOVA.2.')
    && !budovaRequiresSvarPhase(digitalniCesta)
  ) {
    return true;
  }
  return false;
}

export function resolveBudovaZafouknutyPhaseCodeForBuildingType(
  buildingType: BudovaBuildingType | null | undefined,
): string | null {
  if (buildingType === 'PRIPOJENA') return BUDOVA_PRIPOJENA_ZAFOUKNUTO_PHASE_CODE;
  if (buildingType === 'BABOVKA') return BUDOVA_BABOVKA_ZAFOUKNUTO_PHASE_CODE;
  return null;
}

/** PRIPOJENA aggregate post-milestone status (PHASE.1 Připojené plasty). */
export function isPripojenaAggregatePostMilestoneStatus(
  status: string | null | undefined,
): boolean {
  return isSamePhase(status, PHASE_CODE.BUDOVA_PRIPOJENE_PLASTY);
}

/** Parallel milestone identifiers (commit/API use the same uppercase tokens).
 * UI field lists / autoOpenSpec: prefer `milestone-registry.ts` (`BUDOVA_MILESTONE_REGISTRY`). */
export const BUDOVA_MILESTONE_IDS = [
  'ZAVRTANY',
  'ZRIZENE_VR',
  'ODBOCENO',
  'POSTAVENA_BABOVKA',
  'ZAFOUKNUTO',
] as const;
export type BudovaMilestoneId = (typeof BUDOVA_MILESTONE_IDS)[number];

export const BUDOVA_MILESTONE_LABELS: Record<BudovaMilestoneId, string> = {
  ZAVRTANY: 'Zavrtaný',
  ZRIZENE_VR: 'Zřízené VR',
  ODBOCENO: 'Odbočeno',
  POSTAVENA_BABOVKA: 'Připravená bábovka',
  ZAFOUKNUTO: 'Zafouknutý',
};

/**
 * HP / nebyt gate for completing milník Zřízené VR.
 * Parity KMP `BudovaMilestoneEngine.isZrizeneVrSpecValid`.
 */
export function isZrizeneVrSpecValid(
  hpCountTotal: number | null | undefined,
  hpNebytCount: number | null | undefined,
): boolean {
  if (hpCountTotal == null || !Number.isInteger(hpCountTotal) || hpCountTotal < 0) return false;
  if (isMsoHp(hpCountTotal)) {
    if (hpNebytCount == null || !Number.isInteger(hpNebytCount) || hpNebytCount < 0) return false;
    return hpNebytCount <= hpCountTotal;
  }
  return true;
}

/** MSO (HP ≤ 3): milník Zřízené VR nevyžaduje fotku; VSO používá patrové fotky ve spec. */
export function budovaMilestoneSkipsPhotoRequirement(
  milestoneId: BudovaMilestoneId | string | null | undefined,
  hpCountTotal: number | null | undefined,
): boolean {
  if (milestoneId !== 'ZRIZENE_VR') return false;
  if (hpCountTotal == null || !Number.isInteger(hpCountTotal) || hpCountTotal < 0) return false;
  return isMsoHp(hpCountTotal);
}

/**
 * Required parallel milestones per building type (empty for Nerealizované / ZAMITNUTA).
 * PRIPOJENA: Výkresy Milníky @ BUDOVA.2 PHASE.1 — Zřízené VR, Odbočeno, Zavrtaný (pořadí dle Excelu).
 */
export const BUDOVA_REQUIRED_MILESTONES: Record<BudovaBuildingType, readonly BudovaMilestoneId[]> = {
  PRIPOJENA: ['ZRIZENE_VR', 'ODBOCENO', 'ZAVRTANY'],
  BABOVKA: ['ODBOCENO', 'POSTAVENA_BABOVKA'],
  ZAMITNUTA: [],
};

/** Bábovka stage 1 — Zafouknuté unlocks only after both are complete. */
export const BUDOVA_BABOVKA_STAGE1_MILESTONES: readonly BudovaMilestoneId[] = [
  'ODBOCENO',
  'POSTAVENA_BABOVKA',
];

/** `metadata.budovaMilestones` / `metadata.customFields.budovaMilestones` flag shape. */
export interface BudovaMilestonesFlags {
  zavrtany?: boolean;
  zrizeneVr?: boolean;
  odboceno?: boolean;
  postavenaBabovka?: boolean;
  zafouknuto?: boolean;
}

/** Alias used by object metadata typing. */
export type BudovaMilestonesState = BudovaMilestonesFlags;

export const BUDOVA_METADATA_KEYS = {
  budovaMilestones: 'budovaMilestones',
  branchVykopSegmentIds: 'branchVykopSegmentIds',
} as const;

/** Denormalized odbočka geometry keys on BUDOVA metadata (top-level + customFields). */
export const BUDOVA_BRANCH_GEOMETRY_META_KEYS = [
  BUDOVA_METADATA_KEYS.branchVykopSegmentIds,
  'branchJunctionHash',
  'branchDrawnAt',
  'branchOriginKind',
  'branchRozvadecId',
] as const;

/**
 * Metadata patch that clears odbočka links on BUDOVA after spojkoviště / branch VYKOP delete.
 * Leaves milestone flags alone — callers clear ODBOCENO separately when needed.
 */
export function buildBudovaBranchClearMetaPatch(): Record<string, unknown> {
  const cleared: Record<string, unknown> = {
    [BUDOVA_METADATA_KEYS.branchVykopSegmentIds]: [],
    branchJunctionHash: null,
    branchDrawnAt: null,
    branchOriginKind: null,
    branchRozvadecId: null,
  };
  return {
    ...cleared,
    customFields: { ...cleared },
  };
}

/** Branch draw: first vertex may snap to main-route VYKOP / STAVAJICI segments. */
export const BUDOVA_BRANCH_SNAP_OBJECT_TYPES = ['VYKOP', 'STAVAJICI'] as const;
export type BudovaBranchSnapObjectType = (typeof BUDOVA_BRANCH_SNAP_OBJECT_TYPES)[number];

/**
 * Legacy `objects.type` codes map to canonical branch snap types (parity with segment selection).
 * `VYKOP_NOVY` / `VYKOP_METALIKA` → `VYKOP` (legacy VYKOP subtype, not a live object type);
 * `VYKOP_STAVAJICI` → `STAVAJICI`.
 */
export function normalizeBudovaBranchSnapLineType(
  objectType: string | null | undefined,
): BudovaBranchSnapObjectType | null {
  const k = normalizeObjectTypeForLookup(String(objectType ?? '').trim());
  if (k === 'VYKOP_NOVY' || k === 'VYKOP_METALIKA' || k === 'VYKOP') return 'VYKOP';
  if (k === 'VYKOP_STAVAJICI' || k === 'STAVAJICI') return 'STAVAJICI';
  return null;
}

export function isBudovaBranchSnapLineType(objectType: string | null | undefined): boolean {
  return normalizeBudovaBranchSnapLineType(objectType) != null;
}

const MILESTONE_FLAG_KEY: Record<BudovaMilestoneId, keyof BudovaMilestonesFlags> = {
  ZAVRTANY: 'zavrtany',
  ZRIZENE_VR: 'zrizeneVr',
  ODBOCENO: 'odboceno',
  POSTAVENA_BABOVKA: 'postavenaBabovka',
  ZAFOUKNUTO: 'zafouknuto',
};

export interface BudovaMilestoneEngineContext {
  milestones?: BudovaMilestonesFlags | null;
  /**
   * Current BUDOVA status. When Připojená has reached Připojené plasty or later
   * (Zafouknutý / Svařený), Odbočeno junction Připojené gate is waived —
   * that work was not required in the current linear phase.
   */
  currentStatus?: string | null;
  /** Status codes of branch VYKOP child segments (legacy UX / branch-status). */
  branchVykopStatuses?: ReadonlyArray<string | null | undefined>;
  /** Status of junction SPOJKOVISTE linked to BUDOVA (Odbočené engine rule). */
  junctionSpojkovisteStatus?: string | null;
  /**
   * How the odbočka was created at the first vertex.
   * Missing → NEW_SPOJKOVISTE (legacy: always required junction + výkop).
   */
  branchOriginKind?: BudovaBranchOriginKind | null;
  /** This building’s napojené MT barvy on the junction SPOJKOVISTE (NEW / SHARED). */
  junctionNapojeneMtBarvy?: readonly string[] | null;
  /**
   * Admin/superadmin may set Připojené plasty from Nepřipojený without completing
   * parallel milestones. Other roles stay gated.
   */
  isAdmin?: boolean;
  /**
   * Count from `pripojovany_objekt_spec.digitalni_cesta`.
   * Gates Zafouknutý → Svařený (weld only when count > 0).
   */
  digitalniCesta?: number | null;
}

export interface CanAdvanceLinearPhaseResult {
  ok: boolean;
  reason?: string;
}

export function getRequiredMilestones(
  buildingType: BudovaBuildingType | null | undefined,
): readonly BudovaMilestoneId[] {
  if (!buildingType) return [];
  return BUDOVA_REQUIRED_MILESTONES[buildingType] ?? [];
}

/** True when BUDOVA status is Nepřipojený (BUDOVA.2 / BUDOVA.3 PHASE.0). */
export function isBudovaNepripojenyPhase(status: string | null | undefined): boolean {
  return isSamePhase(status, PHASE_CODE.BUDOVA_NEPRIPOJENY);
}

/**
 * Progress milestones (VR / Odbočka / Závrt / Připravená bábovka) are shown only on Nepřipojený
 * for Připojená / Bábovka. Other types/phases hide them even when flags are still open.
 */
export function shouldShowBudovaProgressMilestones(
  buildingType: BudovaBuildingType | null | undefined,
  status: string | null | undefined,
): boolean {
  if (!buildingType || buildingType === 'ZAMITNUTA') return false;
  return isBudovaNepripojenyPhase(status);
}

/**
 * Waiting / „chybí“ for a parallel milestone: only Nepřipojený buildings that require it.
 * BABOVKA hard-fails VR / Závrt; non-Nepřipojený is never waiting.
 */
export function isBudovaWaitingForProgressMilestone(
  buildingType: BudovaBuildingType | null | undefined,
  milestoneId: BudovaMilestoneId,
  status: string | null | undefined,
  ctx: BudovaMilestoneEngineContext = {},
): boolean {
  if (!shouldShowBudovaProgressMilestones(buildingType, status)) return false;
  if (!getRequiredMilestones(buildingType).includes(milestoneId)) return false;
  return !isMilestoneComplete(buildingType, milestoneId, { ...ctx, currentStatus: ctx.currentStatus ?? status });
}

/** Milestones to render in UI (parallel only; Zafouknutý is a linear phase). */
export function getVisibleMilestoneIds(
  buildingType: BudovaBuildingType | null | undefined,
  ctx: BudovaMilestoneEngineContext = {},
): readonly BudovaMilestoneId[] {
  const required = getRequiredMilestones(buildingType);
  if (!required.length) return [];
  const status = ctx.currentStatus;
  if (status != null && String(status).trim() !== '' && !isBudovaNepripojenyPhase(status)) {
    return [];
  }
  return required;
}

/**
 * @deprecated Never auto-enter on fresh open. Use
 * `resolveParallelMilestonePopupModeOnOpen` / `shouldOpenMilestonePickerOnObjectClick`
 * from `milestone-popup-mode.ts` (always picker while incomplete).
 */
export function shouldAutoEnterSingleOpenBudovaMilestone(
  _openCount: number,
  _completedVisibleCount: number,
): boolean {
  return false;
}

function readMilestoneFlag(
  milestones: BudovaMilestonesFlags | null | undefined,
  milestoneId: BudovaMilestoneId,
): boolean {
  return milestones?.[MILESTONE_FLAG_KEY[milestoneId]] === true;
}

/** All branch VYKOP segments reached Vykopaný+ (independent of junction status). */
export function isBranchVykopExcavated(
  branchVykopStatuses: ReadonlyArray<string | null | undefined> | undefined,
): boolean {
  if (!branchVykopStatuses?.length) return false;
  return branchVykopStatuses.every((status) =>
    hasReachedWorkflowMilestone(status, PHASE_CODE.VYKOP_VYKOPANY),
  );
}

/** Junction SPOJKOVISTE reached Připojené+ (independent of branch VYKOP status). */
export function isJunctionSpojkovisteConnected(status: string | null | undefined): boolean {
  return hasReachedWorkflowMilestone(status, PHASE_CODE.SPOJKOVISTE_PRIPOJENE);
}

/**
 * Physical conditions for persisting ODBOCENO (POST milestone / auto-complete).
 * Does not treat the persisted milestone flag as satisfying conditions.
 * Satisfied iff the bound junction SPOJKOVISTE is Připojené — including
 * undrawn/bind-only junctions (no branch VYKOP geometry). Výkop dokopaná /
 * napojené MT / ROZVADEC-only excavation are not criteria.
 */
export function areOdbocenoBranchConditionsMet(
  ctx: BudovaMilestoneEngineContext = {},
): boolean {
  return isJunctionSpojkovisteConnected(ctx.junctionSpojkovisteStatus);
}

/**
 * Selection helper for one-off / SQL backfill after the junction-only Odbočeno rule:
 * building requires ODBOCENO, flag not persisted, bound SPOJKOVISTE is Připojené.
 * Does not skip rows waived by Připojené plasty — those still lack the persisted flag.
 */
export function needsPersistedOdbocenoBackfill(params: {
  buildingType: BudovaBuildingType | null | undefined;
  ctx?: BudovaMilestoneEngineContext;
}): boolean {
  const buildingType = params.buildingType;
  if (!buildingType || buildingType === 'ZAMITNUTA') return false;
  if (!getRequiredMilestones(buildingType).includes('ODBOCENO')) return false;
  const ctx = params.ctx ?? {};
  if (isOdbocenoMilestonePersisted(ctx)) return false;
  return areOdbocenoBranchConditionsMet(ctx);
}

/** Milestone flag already recorded — click should not navigate or re-complete. */
export function isOdbocenoMilestonePersisted(ctx: BudovaMilestoneEngineContext = {}): boolean {
  return readMilestoneFlag(ctx.milestones, 'ODBOCENO');
}

/**
 * Odbočeno / výkop / spojkoviště are not required once Připojená has reached
 * Připojené plasty or a later linear phase (Zafouknutý, Svařený).
 * Those later phases never made odbočka mandatory — do not re-impose it on N→N+1.
 * BABOVKA Zafouknutý does not reach Připojené plasty, so host-phase odbočka stays.
 */
export function isOdbocenoWaivedByPripojenePlastyStatus(
  currentStatus: string | null | undefined,
): boolean {
  return (
    isPripojenaAggregatePostMilestoneStatus(currentStatus)
    || isSamePhase(currentStatus, PHASE_CODE.BUDOVA_SVARENY)
    || (
      isSamePhase(currentStatus, PHASE_CODE.BUDOVA_ZAFOUKNUTO)
      && hasReachedWorkflowMilestone(currentStatus, PHASE_CODE.BUDOVA_PRIPOJENE_PLASTY)
    )
  );
}

/**
 * Picker / Odbočeno UI: treated as done so click does not open výkop or spojkoviště.
 * Persisted flag OR waived by aggregate Připojené plasty status.
 */
export function isOdbocenoSatisfiedForUi(ctx: BudovaMilestoneEngineContext = {}): boolean {
  return (
    isOdbocenoMilestonePersisted(ctx)
    || isOdbocenoWaivedByPripojenePlastyStatus(ctx.currentStatus)
  );
}

/**
 * Odbočené: explicit milestone, waived by Připojené plasty status, OR bound
 * junction SPOJKOVISTE Připojené (drawn or undrawn/bind-only).
 */
export function isOdbocenoComplete(ctx: BudovaMilestoneEngineContext = {}): boolean {
  if (readMilestoneFlag(ctx.milestones, 'ODBOCENO')) return true;
  if (isOdbocenoWaivedByPripojenePlastyStatus(ctx.currentStatus)) return true;
  return areOdbocenoBranchConditionsMet(ctx);
}

export function isMilestoneComplete(
  buildingType: BudovaBuildingType | null | undefined,
  milestoneId: BudovaMilestoneId,
  ctx: BudovaMilestoneEngineContext = {},
): boolean {
  if (!buildingType) return false;
  if (!getRequiredMilestones(buildingType).includes(milestoneId)) return false;

  if (milestoneId === 'ODBOCENO') {
    return isOdbocenoComplete(ctx);
  }

  return readMilestoneFlag(ctx.milestones, milestoneId);
}

export function allMilestonesComplete(
  buildingType: BudovaBuildingType | null | undefined,
  ctx: BudovaMilestoneEngineContext = {},
): boolean {
  const required = getRequiredMilestones(buildingType);
  if (!required.length) return true;
  return required.every((id) => isMilestoneComplete(buildingType, id, ctx));
}

/** Building type may change only until a locking milestone is marked complete. */
/**
 * True when a milestone was completed by a user action (has `completedAt` audit).
 * Bare boolean flags from import/backfill (e.g. orphan `zrizeneVr`) do not count.
 */
export function hasPersistedBudovaMilestoneCompletion(
  milestonesMeta: Pick<BudovaMilestonesMeta, 'completedAt'> | null | undefined,
): boolean {
  const completedAt = milestonesMeta?.completedAt ?? {};
  return Object.values(completedAt).some((v) => typeof v === 'string' && v.trim().length > 0);
}

/**
 * Odbočka stays across type change (Bábovka ↔ Připojená).
 * VR / závrt / připravená bábovka are type-specific — cleared when type change is allowed.
 */
export const BUDOVA_MILESTONES_PRESERVED_ON_BUILDING_TYPE_CHANGE: readonly BudovaMilestoneId[] = [
  'ODBOCENO',
];

/** Type-specific milestones cleared when building type is changed or reverted. */
export const BUDOVA_MILESTONES_REVERTED_ON_BUILDING_TYPE_CHANGE: readonly BudovaMilestoneId[] = [
  'ZAVRTANY',
  'ZRIZENE_VR',
  'POSTAVENA_BABOVKA',
];

function readPersistedCompletedMilestoneIds(
  completedAt: Record<string, string> | null | undefined,
): string[] {
  return Object.entries(completedAt ?? {})
    .filter(([, v]) => typeof v === 'string' && v.trim().length > 0)
    .map(([key]) => key.trim().toUpperCase());
}

/** Work that locks building-type change: VR, závrt, připravená bábovka. Odbočka / záfuk do not. */
const BUILDING_TYPE_CHANGE_BLOCKING_MILESTONES = [
  'ZRIZENE_VR',
  'ZAVRTANY',
  'POSTAVENA_BABOVKA',
] as const;

function hasLegacyBlockingMilestoneFlag(
  milestones: BudovaMilestonesFlags,
): boolean {
  return milestones.zavrtany === true
    || milestones.zrizeneVr === true
    || milestones.postavenaBabovka === true;
}

function hasBlockingCompletedMilestone(
  completedIds: string[],
): boolean {
  return completedIds.some((id) =>
    (BUILDING_TYPE_CHANGE_BLOCKING_MILESTONES as readonly string[]).includes(id),
  );
}

/**
 * Whether building type may still change („Změnit typ budovy“).
 * Blocks only completed VR, závrt, or připravená bábovka. Odbočka and záfuk do not lock.
 * When `milestonesMeta` is provided, only `completedAt` audit counts — orphan backfill flags
 * without timestamps must not block. Legacy callers without meta fall back to boolean flags.
 */
export function canChangeBuildingType(
  milestones: BudovaMilestonesFlags | null | undefined,
  milestonesMeta?: Pick<BudovaMilestonesMeta, 'completedAt'> | null,
): boolean {
  if (milestonesMeta != null) {
    const completedIds = readPersistedCompletedMilestoneIds(milestonesMeta.completedAt);
    return !hasBlockingCompletedMilestone(completedIds);
  }
  if (!milestones) return true;
  return !hasLegacyBlockingMilestoneFlag(milestones);
}

export function canAdvanceLinearPhase(
  buildingType: BudovaBuildingType | null | undefined,
  currentStatus: string | null | undefined,
  targetStatus: string | null | undefined,
  ctx: BudovaMilestoneEngineContext = {},
): CanAdvanceLinearPhaseResult {
  const target = (targetStatus ?? '').trim();
  if (!buildingType || !target) {
    return { ok: false, reason: 'MISSING_BUILDING_TYPE_OR_TARGET' };
  }
  // Prefer explicit ctx.currentStatus; fall back to the transition's current phase so
  // Připojené plasty waive applies even when callers omit currentStatus on the context.
  const milestoneCtx: BudovaMilestoneEngineContext = {
    ...ctx,
    currentStatus: ctx.currentStatus ?? currentStatus,
  };

  if (buildingType === 'ZAMITNUTA') {
    if (
      isSamePhase(currentStatus, PHASE_CODE.BUDOVA_NEPRIPOJENY) &&
      isSamePhase(target, PHASE_CODE.BUDOVA_ODMITNUTY)
    ) {
      return { ok: true };
    }
    if (
      isSamePhase(currentStatus, PHASE_CODE.BUDOVA_ODMITNUTY) &&
      isSamePhase(target, PHASE_CODE.BUDOVA_NEPRIPOJENY)
    ) {
      return { ok: true };
    }
    return { ok: false, reason: 'ZAMITNUTA_INVALID_LINEAR_TARGET' };
  }

  if (buildingType === 'PRIPOJENA') {
    if (
      isSamePhase(currentStatus, PHASE_CODE.BUDOVA_NEPRIPOJENY) &&
      isSamePhase(target, PHASE_CODE.BUDOVA_PRIPOJENE_PLASTY)
    ) {
      if (allMilestonesComplete(buildingType, milestoneCtx) || milestoneCtx.isAdmin === true) {
        return { ok: true };
      }
      return { ok: false, reason: 'MILESTONES_INCOMPLETE' };
    }
    if (
      isSamePhase(currentStatus, PHASE_CODE.BUDOVA_PRIPOJENE_PLASTY) &&
      isSamePhase(target, PHASE_CODE.BUDOVA_ZAFOUKNUTO)
    ) {
      // Past Nepřipojený: milestone UI is hidden — do not keep záfuk gated on leftover flags.
      return { ok: true };
    }
    if (
      isSamePhase(currentStatus, PHASE_CODE.BUDOVA_ZAFOUKNUTO) &&
      isSamePhase(target, PHASE_CODE.BUDOVA_SVARENY)
    ) {
      // Bez digitální cesty končí práce záfukem — sváření až při pozdějším připojení zákazníka.
      if (!budovaRequiresSvarPhase(milestoneCtx.digitalniCesta)) {
        return { ok: false, reason: 'NO_DIGITALNI_CESTA' };
      }
      return { ok: true };
    }
  }

  if (buildingType === 'BABOVKA') {
    if (
      isSamePhase(currentStatus, PHASE_CODE.BUDOVA_NEPRIPOJENY) &&
      isSamePhase(target, PHASE_CODE.BUDOVA_POSTAVENA_BABOVKA)
    ) {
      return { ok: true };
    }
    if (
      isSamePhase(currentStatus, PHASE_CODE.BUDOVA_POSTAVENA_BABOVKA) &&
      isSamePhase(target, PHASE_CODE.BUDOVA_ZAFOUKNUTO)
    ) {
      return { ok: true };
    }
  }

  return { ok: false, reason: 'INVALID_LINEAR_TRANSITION' };
}

/**
 * True when `completedAt` audit map has a non-empty ISO for the milestone id.
 * Current metadata is authoritative — `object_history` is not consulted.
 */
function hasCompletedAtAudit(
  completedAt: Record<string, unknown> | null | undefined,
  milestoneId: BudovaMilestoneId,
): boolean {
  const raw = completedAt?.[milestoneId];
  return typeof raw === 'string' && raw.trim().length > 0;
}

/** Parse `metadata.customFields.budovaMilestones` or top-level mirror. */
export function parseBudovaMilestonesFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): BudovaMilestonesFlags {
  const custom = (metadata?.['customFields'] ?? metadata?.['custom_fields']) as
    | Record<string, unknown>
    | undefined;
  const raw = (custom?.['budovaMilestones'] ?? metadata?.['budovaMilestones']) as
    | Record<string, unknown>
    | undefined;
  if (!raw || typeof raw !== 'object') return {};
  // Flags OR completedAt in the same metadata blob — marking either in DB is enough.
  // Do not require object_history / audited trail beyond what is stored on the object.
  const completedAt =
    raw['completedAt'] && typeof raw['completedAt'] === 'object' && !Array.isArray(raw['completedAt'])
      ? (raw['completedAt'] as Record<string, unknown>)
      : undefined;
  return {
    zavrtany: raw['zavrtany'] === true || hasCompletedAtAudit(completedAt, 'ZAVRTANY'),
    zrizeneVr:
      raw['zrizeneVr'] === true
      || raw['zrizene_vr'] === true
      || hasCompletedAtAudit(completedAt, 'ZRIZENE_VR'),
    odboceno:
      raw['odboceno'] === true
      || raw['odbočeno'] === true
      || hasCompletedAtAudit(completedAt, 'ODBOCENO'),
    postavenaBabovka:
      raw['postavenaBabovka'] === true
      || raw['postavena_babovka'] === true
      || hasCompletedAtAudit(completedAt, 'POSTAVENA_BABOVKA'),
    zafouknuto:
      raw['zafouknuto'] === true
      || raw['zafouknutoParallel'] === true
      || raw['zafouknuto_parallel'] === true
      || hasCompletedAtAudit(completedAt, 'ZAFOUKNUTO'),
  };
}

/** Next UI action for the Odbočené button state machine. */
export type BudovaOdbocenoUiAction =
  | 'PICK_BUILDING_TYPE'
  | 'COMPLETED'
  | 'ENTER_BRANCH_DRAW'
  | 'SHOW_BRANCH_POPUP';

/** Map legacy `budovaWorkflowId` / phase-code branch to building type. */
export function budovaBuildingTypeFromWorkflowBranchId(
  workflowId: number | null | undefined,
): BudovaBuildingType | null {
  if (workflowId === 8) return 'PRIPOJENA';
  if (workflowId === 9) return 'BABOVKA';
  if (workflowId === 7) return 'ZAMITNUTA';
  return null;
}

/** Read explicit `metadata.budovaBuildingType` (top-level or customFields mirror). */
export function readBudovaBuildingTypeFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): BudovaBuildingType | null {
  const custom = (metadata?.['customFields'] ?? metadata?.['custom_fields']) as
    | Record<string, unknown>
    | undefined;
  for (const raw of [metadata?.['budovaBuildingType'], custom?.['budovaBuildingType']]) {
    const s = String(raw ?? '').trim();
    if (isBudovaBuildingType(s)) return s;
  }
  return null;
}

/**
 * Infer building type for legacy/imported BUDOVA rows missing `budovaBuildingType`
 * (e.g. milestones complete under `budovaWorkflowId: 8`).
 */
export function inferBudovaBuildingType(params: {
  metadata?: Record<string, unknown> | null;
  status?: string | null;
  explicitType?: string | null;
}): BudovaBuildingType | null {
  const explicit = params.explicitType ?? readBudovaBuildingTypeFromMetadata(params.metadata ?? null);
  if (isBudovaBuildingType(explicit)) return explicit;

  const variant = parseWorkflowVariant(params.metadata, null);
  const fromWorkflowId = budovaBuildingTypeFromWorkflowBranchId(variant.budovaWorkflowId);
  if (fromWorkflowId) return fromWorkflowId;

  return null;
}

export function hasBudovaMilestoneActivity(ctx: BudovaMilestoneEngineContext = {}): boolean {
  const m = ctx.milestones ?? {};
  return (
    m.zavrtany === true ||
    m.zrizeneVr === true ||
    m.odboceno === true ||
    m.postavenaBabovka === true ||
    m.zafouknuto === true
  );
}

/**
 * Target aggregate PHASE.1 code when advancing from Nepřipojený after all milestones.
 *
 * Must use **building type** workflow branch (PRIPOJENA → BUDOVA.2, BABOVKA → BUDOVA.3),
 * not the status prefix. Legacy/backfill rows can be PRIPOJENA while still on
 * `BUDOVA.1.PHASE.0` (ZAMITNUTA workflow); preserving `.1` would paint complete milestone
 * stripes / auto-advance as `BUDOVA.1.PHASE.1` (Zrušený = black on montáž).
 */
export function resolveBudovaAggregateAdvanceTargetPhaseCode(
  buildingType: BudovaBuildingType,
  currentStatus: string | null | undefined,
): string | null {
  const current = (currentStatus ?? '').trim();
  if (!isSamePhase(current, PHASE_CODE.BUDOVA_NEPRIPOJENY)) return null;
  if (buildingType === 'BABOVKA') return 'BUDOVA.3.PHASE.1';
  if (buildingType === 'PRIPOJENA') return BUDOVA_PRIPOJENA_POST_MILESTONE_PHASE_CODE;
  return null;
}

/**
 * Auto phase after all parallel milestones while still on Nepřipojený (PHASE.0).
 * PRIPOJENA → Připojené plasty; Bábovka → Připravená bábovka.
 * Post-aggregate linear advance (→ Zafouknutý) uses `resolveBudovaLinearNextPhaseCodeAfterMilestones`.
 */
export function resolveBudovaAutoPhaseAfterAllMilestones(params: {
  buildingType: BudovaBuildingType | null | undefined;
  currentStatus: string | null | undefined;
  ctx?: BudovaMilestoneEngineContext;
}): string | null {
  if (!params.buildingType || params.buildingType === 'ZAMITNUTA') return null;
  const ctx = params.ctx ?? {};
  const current = params.currentStatus ?? '';
  if (!isSamePhase(current, PHASE_CODE.BUDOVA_NEPRIPOJENY)) return null;
  if (!allMilestonesComplete(params.buildingType, ctx)) return null;
  const targetName =
    params.buildingType === 'BABOVKA'
      ? PHASE_CODE.BUDOVA_POSTAVENA_BABOVKA
      : PHASE_CODE.BUDOVA_PRIPOJENE_PLASTY;
  const targetCode = resolveBudovaAggregateAdvanceTargetPhaseCode(params.buildingType, current);
  if (!targetCode) return null;
  return canAdvanceLinearPhase(params.buildingType, current, targetName, ctx).ok ? targetCode : null;
}

/** True when parallel milestones are shown/completed (Výkresy Milníky @ PHASE.1). */
export function isBudovaMilestoneHostPhase(
  buildingType: BudovaBuildingType | null | undefined,
  currentStatus: string | null | undefined,
): boolean {
  if (!buildingType || buildingType === 'ZAMITNUTA') return false;
  const current = currentStatus ?? '';
  if (buildingType === 'PRIPOJENA') {
    return isPripojenaAggregatePostMilestoneStatus(current);
  }
  if (buildingType === 'BABOVKA') {
    return isSamePhase(current, PHASE_CODE.BUDOVA_POSTAVENA_BABOVKA);
  }
  return false;
}

/**
 * True when UI should replace Excel linear `getNextPhases` with the milestone engine
 * (Nepřipojený → aggregate PHASE.1, milestones on PHASE.1, or post-milestone linear).
 */
export function shouldFilterEditableNextPhasesForBudovaMilestones(params: {
  objectType: string | null | undefined;
  currentStatus: string | null | undefined;
  buildingType: BudovaBuildingType | null | undefined;
}): boolean {
  if (String(params.objectType ?? '').toUpperCase() !== 'BUDOVA') return false;
  const current = params.currentStatus ?? '';
  if (isSamePhase(current, PHASE_CODE.BUDOVA_NEPRIPOJENY)) return true;
  const buildingType = params.buildingType;
  if (!buildingType || buildingType === 'ZAMITNUTA') return false;
  if (buildingType === 'PRIPOJENA') {
    return (
      isPripojenaAggregatePostMilestoneStatus(current)
      || isSamePhase(current, PHASE_CODE.BUDOVA_ZAFOUKNUTO)
    );
  }
  if (buildingType === 'BABOVKA') {
    return (
      isSamePhase(current, PHASE_CODE.BUDOVA_POSTAVENA_BABOVKA)
      || isSamePhase(current, PHASE_CODE.BUDOVA_ZAFOUKNUTO)
    );
  }
  return false;
}

/** Next linear phase after milestones on aggregate PHASE.1 (Připojená/Bábovka → Zafouknutý). */
export function resolveBudovaLinearNextPhaseCodeAfterMilestones(params: {
  buildingType: BudovaBuildingType | null | undefined;
  currentStatus: string | null | undefined;
  ctx?: BudovaMilestoneEngineContext;
}): string | null {
  if (!params.buildingType || params.buildingType === 'ZAMITNUTA') return null;
  const ctx = params.ctx ?? {};
  const current = params.currentStatus ?? '';
  if (!isBudovaMilestoneHostPhase(params.buildingType, current)) return null;
  if (!allMilestonesComplete(params.buildingType, ctx)) return null;
  const targetSemantic = PHASE_CODE.BUDOVA_ZAFOUKNUTO;
  if (!canAdvanceLinearPhase(params.buildingType, current, targetSemantic, ctx).ok) return null;
  return resolveBudovaZafouknutyPhaseCodeForBuildingType(params.buildingType);
}

/**
 * Explicit admin shortcut: Nepřipojený → Připojené plasty while parallel milestones
 * are still open. Same persisted PHASE.1 as a normal milestone auto-advance.
 */
export function buildBudovaAdminPripojeneOverridePhase(params: {
  buildingType: BudovaBuildingType | null | undefined;
  currentStatus: string | null | undefined;
  ctx?: BudovaMilestoneEngineContext;
}): { code: string; name: string } | null {
  if (params.buildingType !== 'PRIPOJENA') return null;
  if (params.ctx?.isAdmin !== true) return null;
  const current = params.currentStatus ?? '';
  if (!isSamePhase(current, PHASE_CODE.BUDOVA_NEPRIPOJENY)) return null;
  const ctx = params.ctx ?? {};
  if (allMilestonesComplete(params.buildingType, ctx)) return null;
  if (!canAdvanceLinearPhase(params.buildingType, current, PHASE_CODE.BUDOVA_PRIPOJENE_PLASTY, ctx).ok) {
    return null;
  }
  const code = resolveBudovaAggregateAdvanceTargetPhaseCode(params.buildingType, current);
  if (!code) return null;
  return { code, name: getPhaseName(code) };
}

/** WorkflowPhase-shaped next step when milestone engine unlocks Zafouknutý. */
export function buildBudovaLinearNextPhaseAfterMilestones(params: {
  buildingType: BudovaBuildingType | null | undefined;
  currentStatus: string | null | undefined;
  ctx?: BudovaMilestoneEngineContext;
}): { code: string; name: string } | null {
  const code = resolveBudovaLinearNextPhaseCodeAfterMilestones(params);
  if (!code) return null;
  return { code, name: getPhaseName(code) };
}

/** Nepřipojený phase code when reverting from aggregate post-milestone status. */
export function resolveBudovaAggregateRevertTargetPhaseCode(params: {
  buildingType: BudovaBuildingType | null | undefined;
  currentStatus: string | null | undefined;
}): string | null {
  if (!params.buildingType || params.buildingType === 'ZAMITNUTA') return null;
  const current = (params.currentStatus ?? '').trim();
  if (params.buildingType === 'PRIPOJENA' && isPripojenaAggregatePostMilestoneStatus(current)) {
    const dot = current.indexOf('.PHASE.');
    return dot > 0 ? `${current.slice(0, dot)}.PHASE.0` : PHASE_CODE.BUDOVA_NEPRIPOJENY;
  }
  if (
    params.buildingType === 'BABOVKA' &&
    isSamePhase(current, PHASE_CODE.BUDOVA_POSTAVENA_BABOVKA)
  ) {
    return BUDOVA_BABOVKA_NEPRIPOJENY_PHASE_CODE;
  }
  return null;
}

/**
 * Zpět from Připojené plasty / Připravená bábovka → Nepřipojený.
 */
export function buildBudovaAggregatePreviousPhaseToNepripojeny(params: {
  buildingType: BudovaBuildingType | null | undefined;
  currentStatus: string | null | undefined;
}): { code: string; name: string } | null {
  const code = resolveBudovaAggregateRevertTargetPhaseCode(params);
  if (!code) return null;
  if (
    !isBudovaAggregatePhaseRevertToNepripojeny(
      params.buildingType,
      params.currentStatus,
      code,
    )
  ) {
    return null;
  }
  return { code, name: getPhaseName(code) };
}

/** Replace legacy linear previous phase with Nepřipojený on aggregate post-milestone status. */
export function filterPreviousPhasesForBudovaMilestones<T extends { code: string; name?: string }>(
  previousPhases: readonly T[],
  params: {
    buildingType: BudovaBuildingType | null | undefined;
    currentStatus: string | null | undefined;
  },
): T[] {
  const synthetic = buildBudovaAggregatePreviousPhaseToNepripojeny(params);
  if (!synthetic) return [...previousPhases];
  const template = previousPhases[0];
  if (template) {
    return [{ ...template, code: synthetic.code, name: synthetic.name }];
  }
  return [{ code: synthetic.code, name: synthetic.name } as T];
}

/** Map object fields used to discover dokop VYKOP segments linked to a BUDOVA. */
export interface BranchVykopMapLookup {
  id: string;
  type?: string | null;
  metadata?: unknown;
  parentObjectId?: string | null;
}

/** Read denormalized `branchVykopSegmentIds` from BUDOVA metadata (top-level or customFields). */
export function readBranchVykopSegmentIdsFromMetadata(
  metadata: unknown,
): string[] {
  if (metadata == null || typeof metadata !== 'object' || Array.isArray(metadata)) return [];
  const m = metadata as Record<string, unknown>;
  const custom = (m['customFields'] ?? m['custom_fields']) as Record<string, unknown> | undefined;
  const raw = custom?.[BUDOVA_METADATA_KEYS.branchVykopSegmentIds]
    ?? m[BUDOVA_METADATA_KEYS.branchVykopSegmentIds];
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
}

function normalizeBranchVykopSegmentIdList(
  ids: readonly string[] | null | undefined,
): string[] {
  if (!ids?.length) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of ids) {
    const id = String(raw ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * Resolve branch VYKOP segment ids for Odbočené UI:
 * 1) GET branch-status when loaded (including empty = no live odbočka),
 * 2) else BUDOVA metadata cache, 3) visible map objects (dokop VYKOP parent link).
 */
export function resolveBranchVykopSegmentIds(params: {
  branchStatusSegmentIds?: readonly string[] | null;
  budovaMetadata?: unknown;
  budovaId?: string | null;
  mapObjects?: readonly BranchVykopMapLookup[];
}): string[] {
  // Loaded GET /branch-status is authoritative, including []. Soft-deleted odbočka
  // must not keep ENTER_BRANCH_DRAW blocked via stale bbox `branchVykopSegmentIds`.
  if (params.branchStatusSegmentIds != null) {
    return normalizeBranchVykopSegmentIdList(params.branchStatusSegmentIds);
  }

  const fromMeta = readBranchVykopSegmentIdsFromMetadata(params.budovaMetadata);
  if (fromMeta.length > 0) return fromMeta;

  const budovaId = String(params.budovaId ?? '').trim();
  const mapObjects = params.mapObjects;
  if (!budovaId || !mapObjects?.length) return [];

  const fromMap = mapObjects
    .map((obj) => {
      const id = String(obj.id ?? '').trim();
      if (!id) return null;
      return resolveBranchParentBudovaId({
        type: obj.type,
        metadata: obj.metadata,
        parentObjectId: obj.parentObjectId,
      }) === budovaId
        ? id
        : null;
    })
    .filter((id): id is string => id != null);

  return normalizeBranchVykopSegmentIdList(fromMap).sort();
}

/**
 * Overlay GET /branch-status milestone flags onto bbox metadata.
 * `odboceno` uses the live payload only — OR with stale bbox would hide
 * „Vykreslit odbočku“ after odbočka delete (Připojená i Bábovka).
 */
export function mergeBudovaMilestoneFlagsFromBranchStatus(
  fromMeta: BudovaMilestonesFlags,
  branchMilestones: Record<string, boolean> | null | undefined,
): BudovaMilestonesFlags {
  if (!branchMilestones) return fromMeta;
  return {
    zavrtany: fromMeta.zavrtany === true || branchMilestones['zavrtany'] === true,
    zrizeneVr: fromMeta.zrizeneVr === true || branchMilestones['zrizeneVr'] === true,
    odboceno: branchMilestones['odboceno'] === true,
    postavenaBabovka:
      fromMeta.postavenaBabovka === true || branchMilestones['postavenaBabovka'] === true,
    zafouknuto: fromMeta.zafouknuto === true || branchMilestones['zafouknuto'] === true,
  };
}

export function resolveOdbocenoUiAction(
  buildingType: BudovaBuildingType | null | undefined,
  ctx: BudovaMilestoneEngineContext & { branchVykopSegmentIds?: readonly string[] },
): BudovaOdbocenoUiAction {
  if (!buildingType) return 'PICK_BUILDING_TYPE';
  if (buildingType === 'ZAMITNUTA') return 'COMPLETED';
  // Persisted or already on Připojené plasty — never force výkop / spojkoviště.
  if (isOdbocenoSatisfiedForUi(ctx)) return 'COMPLETED';
  const hasBranch = (ctx.branchVykopSegmentIds?.length ?? 0) > 0;
  const hasBoundJunction =
    ctx.junctionSpojkovisteStatus != null
    && String(ctx.junctionSpojkovisteStatus).trim() !== '';
  // Bind-only junction (no drawn odbočka) still opens spojkoviště / complete flow.
  if (!hasBranch && !hasBoundJunction) {
    return 'ENTER_BRANCH_DRAW';
  }
  return 'SHOW_BRANCH_POPUP';
}

/**
 * Branch-status fields used to pick the next Odbočeno child (výkop vs spojkoviště).
 * Mirrors GET `/objects/budova/:id/branch-status`.
 */
export interface OdbocenoBranchWorkStatus {
  vykopExcavated?: boolean | null;
  vykopSegmentStatuses?: ReadonlyArray<string | null | undefined> | null;
  junctionConnected?: boolean | null;
  junctionSpojkovisteStatus?: string | null;
  junctionRequiredForOdboceno?: boolean | null;
  branchOriginKind?: string | null;
  junctionSpojkovisteNapojeneMtBarvy?: readonly string[] | null;
  junctionSpojkovisteNapojeneMtBarva?: string | null;
}

/** Next child to open from milník Odbočeno (SHOW_BRANCH_POPUP). */
export type OdbocenoNavigateTarget = 'VYKOP' | 'SPOJKOVISTE' | 'COMPLETE';

/** Branch VYKOP reached Vykopaný+ (`vykopExcavated` or segment statuses). */
export function isOdbocenoVykopDone(
  status: OdbocenoBranchWorkStatus | null | undefined,
): boolean {
  if (!status) return false;
  if (status.vykopExcavated === true) return true;
  return isBranchVykopExcavated(status.vykopSegmentStatuses ?? undefined);
}

/**
 * Junction work for Odbočeno: bound SPOJKOVISTE reached Připojené+.
 * Falls back to `junctionSpojkovisteStatus` when `junctionConnected` is missing.
 * Výkop / napojené MT / ROZVADEC exceptions are not part of this gate.
 */
export function isOdbocenoSpojDone(
  status: OdbocenoBranchWorkStatus | null | undefined,
): boolean {
  if (!status) return false;
  return (
    status.junctionConnected === true ||
    isJunctionSpojkovisteConnected(status.junctionSpojkovisteStatus)
  );
}

/**
 * Navigate for milník Odbočeno: only junction Připojené matters.
 * Unfinished výkop no longer blocks / redirects — bind-only junctions count.
 */
export function resolveOdbocenoNavigateTarget(
  status: OdbocenoBranchWorkStatus | null | undefined,
): OdbocenoNavigateTarget {
  if (isOdbocenoSpojDone(status)) return 'COMPLETE';
  return 'SPOJKOVISTE';
}

/** Same 1 h window as phase revert (`PHASE_REVERT_OWN_WINDOW_MS` in object-status.utils). */
export const MILESTONE_REVERT_OWN_WINDOW_MS = 60 * 60 * 1000;

/** Bábovka Nepřipojený workflow code (milestone mode). */
export const BUDOVA_BABOVKA_NEPRIPOJENY_PHASE_CODE = 'BUDOVA.3.PHASE.0';

export interface BudovaOdbocenoTriggerMeta {
  objectId: string;
  objectType: 'VYKOP' | 'SPOJKOVISTE';
  completedAt: string;
}

/** Extended milestone metadata (flags + audit fields in `metadata.budovaMilestones`). */
export interface BudovaMilestonesMeta {
  flags: BudovaMilestonesFlags;
  completedAt: Partial<Record<BudovaMilestoneId, string>>;
  completedBy: Partial<Record<BudovaMilestoneId, string>>;
  odbocenoTrigger?: BudovaOdbocenoTriggerMeta;
  lastCompletedMilestoneId?: BudovaMilestoneId;
  lastCompletedAt?: string;
}

const MILESTONE_REVERT_ORDER: readonly BudovaMilestoneId[] = [
  'ZRIZENE_VR',
  'ODBOCENO',
  'ZAVRTANY',
  'POSTAVENA_BABOVKA',
];

/** Raw `budovaMilestones` object from metadata (top-level or customFields mirror). */
export function readRawBudovaMilestonesObject(
  metadata: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const custom = (metadata?.['customFields'] ?? metadata?.['custom_fields']) as
    | Record<string, unknown>
    | undefined;
  const raw = (custom?.['budovaMilestones'] ?? metadata?.['budovaMilestones']) as
    | Record<string, unknown>
    | undefined;
  return raw && typeof raw === 'object' ? raw : {};
}

function readStringRecord(
  raw: Record<string, unknown>,
  key: string,
): Partial<Record<BudovaMilestoneId, string>> {
  const v = raw[key];
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  const out: Partial<Record<BudovaMilestoneId, string>> = {};
  for (const id of BUDOVA_MILESTONE_IDS) {
    const s = (v as Record<string, unknown>)[id];
    if (typeof s === 'string' && s.trim()) out[id] = s.trim();
  }
  return out;
}

function parseOdbocenoTrigger(raw: Record<string, unknown>): BudovaOdbocenoTriggerMeta | undefined {
  const t = raw['odbocenoTrigger'];
  if (!t || typeof t !== 'object' || Array.isArray(t)) return undefined;
  const rec = t as Record<string, unknown>;
  const objectId = String(rec['objectId'] ?? '').trim();
  const objectType = String(rec['objectType'] ?? '').trim().toUpperCase();
  const completedAt = String(rec['completedAt'] ?? '').trim();
  if (!objectId || (objectType !== 'VYKOP' && objectType !== 'SPOJKOVISTE') || !completedAt) {
    return undefined;
  }
  return { objectId, objectType: objectType as 'VYKOP' | 'SPOJKOVISTE', completedAt };
}

/** Parse milestone flags + completion audit from object metadata. */
export function parseBudovaMilestonesMeta(
  metadata: Record<string, unknown> | null | undefined,
): BudovaMilestonesMeta {
  const raw = readRawBudovaMilestonesObject(metadata);
  const flags = parseBudovaMilestonesFromMetadata(metadata);
  const lastId = raw['lastCompletedMilestoneId'];
  const lastCompletedMilestoneId =
    typeof lastId === 'string' && (BUDOVA_MILESTONE_IDS as readonly string[]).includes(lastId)
      ? (lastId as BudovaMilestoneId)
      : undefined;
  const lastAt = raw['lastCompletedAt'];
  return {
    flags,
    completedAt: readStringRecord(raw, 'completedAt'),
    completedBy: readStringRecord(raw, 'completedBy'),
    odbocenoTrigger: parseOdbocenoTrigger(raw),
    lastCompletedMilestoneId,
    lastCompletedAt: typeof lastAt === 'string' && lastAt.trim() ? lastAt.trim() : undefined,
  };
}

function milestoneRevertOrderIndex(id: BudovaMilestoneId): number {
  const i = MILESTONE_REVERT_ORDER.indexOf(id);
  return i >= 0 ? i : MILESTONE_REVERT_ORDER.length;
}

/** Last completed milestone by `lastCompletedMilestoneId` or max `completedAt` (tie-break by fixed order). */
export function resolveLastCompletedMilestone(
  meta: BudovaMilestonesMeta,
  buildingType: BudovaBuildingType | null | undefined,
): BudovaMilestoneId | null {
  if (!buildingType || buildingType === 'ZAMITNUTA') return null;
  const required = getRequiredMilestones(buildingType);
  const completedIds = required.filter((id) => readMilestoneFlag(meta.flags, id));
  if (!completedIds.length) return null;

  if (
    meta.lastCompletedMilestoneId &&
    completedIds.includes(meta.lastCompletedMilestoneId)
  ) {
    return meta.lastCompletedMilestoneId;
  }

  let best: BudovaMilestoneId | null = null;
  let bestTs = Number.NEGATIVE_INFINITY;
  for (const id of completedIds) {
    const at = meta.completedAt[id];
    const ts = at ? Date.parse(at) : Number.NaN;
    if (!Number.isFinite(ts)) continue;
    if (
      best == null ||
      ts > bestTs ||
      (ts === bestTs && milestoneRevertOrderIndex(id) > milestoneRevertOrderIndex(best))
    ) {
      best = id;
      bestTs = ts;
    }
  }
  if (best) return best;

  // Legacy data bez `completedAt` timestampů — fallback na poslední dokončený milník dle
  // pevného pořadí revertu (stejné tie-break pořadí jako timestamp větev výše). Bez tohoto
  // fallbacku by revert agregátní fáze neodznačil žádný milník a objekt by se okamžitě
  // auto-povýšil zpět na agregátní fázi (smyčka).
  let fallback: BudovaMilestoneId | null = null;
  for (const id of completedIds) {
    if (fallback == null || milestoneRevertOrderIndex(id) > milestoneRevertOrderIndex(fallback)) {
      fallback = id;
    }
  }
  return fallback;
}

/**
 * Completer within 1h, or privileged role, may uncomplete a parallel milestone.
 * Pass `isAdmin` from `hasAdminOrSuperAdminRole` (admin / superadmin).
 */
export function canRevertMilestone(params: {
  milestoneId: BudovaMilestoneId;
  meta: BudovaMilestonesMeta;
  userEmail: string | null | undefined;
  isAdmin: boolean;
  now?: number;
}): boolean {
  const { milestoneId, meta, userEmail, isAdmin } = params;
  if (isAdmin) {
    return readMilestoneFlag(meta.flags, milestoneId);
  }
  const completedAt = meta.completedAt[milestoneId];
  const completedBy = meta.completedBy[milestoneId];
  if (!completedAt || !completedBy || !userEmail?.trim()) return false;
  if (!readMilestoneFlag(meta.flags, milestoneId)) return false;
  if (userEmail.trim().toLowerCase() !== completedBy.trim().toLowerCase()) return false;
  const ts = Date.parse(completedAt);
  if (!Number.isFinite(ts)) return false;
  const now = params.now ?? Date.now();
  return now - ts <= MILESTONE_REVERT_OWN_WINDOW_MS;
}

/**
 * Picker click: incomplete is always pickable; completed only when
 * {@link canRevertMilestone} (privileged role or completer within 1h).
 */
export function canPickBudovaMilestone(params: {
  complete: boolean;
  milestoneId: BudovaMilestoneId;
  meta: BudovaMilestonesMeta;
  userEmail: string | null | undefined;
  isAdmin: boolean;
  now?: number;
}): boolean {
  if (!params.complete) return true;
  return canRevertMilestone(params);
}

export interface BudovaMilestonePickerRow {
  id: BudovaMilestoneId;
  label: string;
  /** Logical complete for click/disable (ODBOCENO = persisted / waived, not physical gates). */
  complete: boolean;
  /** Visual „hotovo“ including ODBOCENO physical branch conditions. */
  done: boolean;
  revertable: boolean;
  pickable: boolean;
}

/**
 * All required milestones while Nepřipojený (3 Připojená / 2 Bábovka).
 * Completed rows stay visible; `pickable` follows {@link canPickBudovaMilestone}.
 */
export function buildBudovaMilestonePickerRows(params: {
  buildingType: BudovaBuildingType | null | undefined;
  ctx?: BudovaMilestoneEngineContext;
  meta: BudovaMilestonesMeta;
  userEmail: string | null | undefined;
  isAdmin: boolean;
  now?: number;
}): BudovaMilestonePickerRow[] {
  const ctx = params.ctx ?? {};
  return getVisibleMilestoneIds(params.buildingType, ctx).map((id) => {
    const complete =
      id === 'ODBOCENO'
        ? isOdbocenoSatisfiedForUi(ctx)
        : isMilestoneComplete(params.buildingType, id, ctx);
    const done = isMilestoneComplete(params.buildingType, id, ctx);
    const revertable = canRevertMilestone({
      milestoneId: id,
      meta: params.meta,
      userEmail: params.userEmail,
      isAdmin: params.isAdmin,
      now: params.now,
    });
    return {
      id,
      label: BUDOVA_MILESTONE_LABELS[id],
      complete,
      done,
      revertable,
      pickable: canPickBudovaMilestone({
        complete,
        milestoneId: id,
        meta: params.meta,
        userEmail: params.userEmail,
        isAdmin: params.isAdmin,
        now: params.now,
      }),
    };
  });
}

/** When uncompleting a milestone clears aggregate auto-phase, return target Nepřipojený code. */
export function shouldRollbackStatusOnMilestoneRevert(
  buildingType: BudovaBuildingType | null | undefined,
  currentStatus: string | null | undefined,
  flagsAfterRevert: BudovaMilestonesFlags,
  ctx?: BudovaMilestoneEngineContext,
): string | null {
  if (!buildingType || buildingType === 'ZAMITNUTA') return null;
  const ctxAfter: BudovaMilestoneEngineContext = { ...(ctx ?? {}), milestones: flagsAfterRevert };
  if (allMilestonesComplete(buildingType, ctxAfter)) return null;
  const current = (currentStatus ?? '').trim();
  const isPostAggregate =
    (buildingType === 'PRIPOJENA' &&
      (isPripojenaAggregatePostMilestoneStatus(current)
        || isSamePhase(current, PHASE_CODE.BUDOVA_ZAFOUKNUTO))) ||
    (buildingType === 'BABOVKA' &&
      (isSamePhase(current, PHASE_CODE.BUDOVA_POSTAVENA_BABOVKA)
        || isSamePhase(current, PHASE_CODE.BUDOVA_ZAFOUKNUTO)));
  if (!isPostAggregate) return null;
  return buildingType === 'BABOVKA'
    ? BUDOVA_BABOVKA_NEPRIPOJENY_PHASE_CODE
    : PHASE_CODE.BUDOVA_NEPRIPOJENY;
}

/** True when backward phase from aggregate post-milestone to Nepřipojený should uncomplete last milestone. */
export function isBudovaAggregatePhaseRevertToNepripojeny(
  buildingType: BudovaBuildingType | null | undefined,
  currentStatus: string | null | undefined,
  targetStatus: string | null | undefined,
): boolean {
  if (!buildingType || buildingType === 'ZAMITNUTA') return false;
  const current = (currentStatus ?? '').trim();
  const target = (targetStatus ?? '').trim();
  if (!isSamePhase(target, PHASE_CODE.BUDOVA_NEPRIPOJENY)) return false;
  if (buildingType === 'PRIPOJENA' && isPripojenaAggregatePostMilestoneStatus(current)) {
    return true;
  }
  if (buildingType === 'BABOVKA' && isSamePhase(current, PHASE_CODE.BUDOVA_POSTAVENA_BABOVKA)) {
    return true;
  }
  return false;
}

/** True when ZAMITNUTA backward phase Zrušená → Nepřipojený should clear building type (phase revert). */
export function isZamitnutaRejectionPhaseRevert(
  buildingType: BudovaBuildingType | null | undefined,
  currentStatus: string | null | undefined,
  targetStatus: string | null | undefined,
): boolean {
  if (buildingType !== 'ZAMITNUTA') return false;
  return (
    isSamePhase(currentStatus, PHASE_CODE.BUDOVA_ODMITNUTY) &&
    isSamePhase(targetStatus, PHASE_CODE.BUDOVA_NEPRIPOJENY)
  );
}

/** Merge completion audit into raw `budovaMilestones` object (caller persists to metadata). */
export function buildMilestoneCompleteMetaPatch(params: {
  existingRaw: Record<string, unknown>;
  flags: BudovaMilestonesFlags;
  milestoneId: BudovaMilestoneId;
  userEmail: string;
  completedAtIso: string;
  odbocenoTrigger?: BudovaOdbocenoTriggerMeta;
}): Record<string, unknown> {
  const completedAt = {
    ...(readStringRecord(params.existingRaw, 'completedAt') as Record<string, string>),
    [params.milestoneId]: params.completedAtIso,
  };
  const completedBy = {
    ...(readStringRecord(params.existingRaw, 'completedBy') as Record<string, string>),
    [params.milestoneId]: params.userEmail.trim(),
  };
  return {
    ...params.flags,
    completedAt,
    completedBy,
    lastCompletedMilestoneId: params.milestoneId,
    lastCompletedAt: params.completedAtIso,
    ...(params.milestoneId === 'ODBOCENO' && params.odbocenoTrigger
      ? { odbocenoTrigger: params.odbocenoTrigger }
      : {}),
  };
}

/** Clear milestone flag + audit fields after revert. */
export function buildMilestoneRevertMetaPatch(params: {
  existingRaw: Record<string, unknown>;
  flags: BudovaMilestonesFlags;
  milestoneId: BudovaMilestoneId;
}): Record<string, unknown> {
  const completedAt = { ...readStringRecord(params.existingRaw, 'completedAt') };
  const completedBy = { ...readStringRecord(params.existingRaw, 'completedBy') };
  delete completedAt[params.milestoneId];
  delete completedBy[params.milestoneId];

  const remaining = (BUDOVA_MILESTONE_IDS as readonly BudovaMilestoneId[]).filter(
    (id) => readMilestoneFlag(params.flags, id),
  );
  let lastCompletedMilestoneId: BudovaMilestoneId | undefined;
  let lastCompletedAt: string | undefined;
  let bestTs = Number.NEGATIVE_INFINITY;
  for (const id of remaining) {
    const at = completedAt[id];
    const ts = at ? Date.parse(at) : Number.NaN;
    if (!Number.isFinite(ts)) continue;
    if (
      !lastCompletedMilestoneId ||
      ts > bestTs ||
      (ts === bestTs &&
        milestoneRevertOrderIndex(id) > milestoneRevertOrderIndex(lastCompletedMilestoneId))
    ) {
      lastCompletedMilestoneId = id;
      lastCompletedAt = at;
      bestTs = ts;
    }
  }

  const patch: Record<string, unknown> = {
    ...params.flags,
    completedAt,
    completedBy,
  };
  if (lastCompletedMilestoneId) {
    patch['lastCompletedMilestoneId'] = lastCompletedMilestoneId;
    patch['lastCompletedAt'] = lastCompletedAt;
  } else {
    delete patch['lastCompletedMilestoneId'];
    delete patch['lastCompletedAt'];
  }
  if (params.milestoneId === 'ODBOCENO') {
    delete patch['odbocenoTrigger'];
  }
  return patch;
}

/** Audit fields for first-click building type selection (stored in metadata). */
export interface BudovaBuildingTypeSelectionMeta {
  setAt: string;
  setBy: string;
}

const BUDOVA_BUILDING_TYPE_SET_AT_KEY = 'budovaBuildingTypeSetAt';
const BUDOVA_BUILDING_TYPE_SET_BY_KEY = 'budovaBuildingTypeSetBy';

function readBuildingTypeSelectionField(
  metadata: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined {
  const custom = (metadata?.['customFields'] ?? metadata?.['custom_fields']) as
    | Record<string, unknown>
    | undefined;
  for (const raw of [metadata?.[key], custom?.[key]]) {
    const s = String(raw ?? '').trim();
    if (s) return s;
  }
  return undefined;
}

/** Parse building-type selection audit from object metadata. */
export function parseBudovaBuildingTypeSelectionMeta(
  metadata: Record<string, unknown> | null | undefined,
): BudovaBuildingTypeSelectionMeta | null {
  const setAt = readBuildingTypeSelectionField(metadata, BUDOVA_BUILDING_TYPE_SET_AT_KEY);
  const setBy = readBuildingTypeSelectionField(metadata, BUDOVA_BUILDING_TYPE_SET_BY_KEY);
  if (!setAt || !setBy) return null;
  return { setAt, setBy };
}

/**
 * Whether user may revert / change building type.
 * Same work gate as [canChangeBuildingType] — VR / závrt / připravená bábovka lock.
 * Odbočka does not lock. Not gated by the 1 h selector window / admin (that is milestone revert).
 */
export function canRevertBuildingType(params: {
  buildingType: BudovaBuildingType | null | undefined;
  milestones: BudovaMilestonesFlags | null | undefined;
  /** Prefer this over bare flags — orphan import/backfill flags without completedAt do not lock. */
  milestonesMeta?: Pick<BudovaMilestonesMeta, 'completedAt'> | null;
  /** Kept for call-site compatibility; type revert is not 1h/admin-gated. */
  selectionMeta?: BudovaBuildingTypeSelectionMeta | null;
  userEmail?: string | null | undefined;
  isAdmin?: boolean;
  now?: number;
}): boolean {
  const { buildingType, milestones, milestonesMeta } = params;
  if (!buildingType || !isBudovaBuildingType(buildingType)) return false;
  return canChangeBuildingType(milestones, milestonesMeta);
}

/** Metadata patch when building type is first selected. */
export function buildBuildingTypeSetMetaPatch(params: {
  buildingType: BudovaBuildingType;
  userEmail: string;
  setAtIso: string;
  workflowId: number;
}): Record<string, unknown> {
  return {
    budovaBuildingType: params.buildingType,
    budovaWorkflowId: params.workflowId,
    [BUDOVA_BUILDING_TYPE_SET_AT_KEY]: params.setAtIso,
    [BUDOVA_BUILDING_TYPE_SET_BY_KEY]: params.userEmail.trim(),
    customFields: {
      budovaBuildingType: params.buildingType,
      budovaWorkflowId: params.workflowId,
      [BUDOVA_BUILDING_TYPE_SET_AT_KEY]: params.setAtIso,
      [BUDOVA_BUILDING_TYPE_SET_BY_KEY]: params.userEmail.trim(),
    },
  };
}

/**
 * Milestone blob for type change / revert: keep odbočka (+ audit / trigger),
 * clear VR / závrt / připravená bábovka / zafouknuto. Branch geometry keys live
 * outside this blob and are left alone by the type-change merge.
 */
export function buildMilestonesPreservingOdbocenoForTypeChange(
  existingMetadata?: Record<string, unknown> | null,
): Record<string, unknown> {
  const parsed = parseBudovaMilestonesMeta(existingMetadata);
  const raw = readRawBudovaMilestonesObject(existingMetadata);
  const odboceno = parsed.flags.odboceno === true;
  const completedAt: Record<string, string> = {};
  const completedBy: Record<string, string> = {};
  if (odboceno) {
    const at = parsed.completedAt['ODBOCENO'];
    const by = parsed.completedBy['ODBOCENO'];
    if (at) completedAt['ODBOCENO'] = at;
    if (by) completedBy['ODBOCENO'] = by;
  }
  const blob: Record<string, unknown> = {
    zavrtany: false,
    zrizeneVr: false,
    odboceno,
    postavenaBabovka: false,
    zafouknuto: false,
    completedAt,
    completedBy,
  };
  if (odboceno && raw['odbocenoTrigger']) {
    blob['odbocenoTrigger'] = raw['odbocenoTrigger'];
  }
  if (odboceno && (parsed.lastCompletedMilestoneId === 'ODBOCENO' || completedAt['ODBOCENO'])) {
    blob['lastCompletedMilestoneId'] = 'ODBOCENO';
    blob['lastCompletedAt'] = completedAt['ODBOCENO'] ?? parsed.lastCompletedAt;
  }
  return blob;
}

/** True when metadata carries an explicit building-type revert (local patch or server). */
export function isExplicitBudovaBuildingTypeReset(
  metadata: Record<string, unknown> | null | undefined,
): boolean {
  if (!metadata) return false;
  const custom = metadata['customFields'] as Record<string, unknown> | undefined;
  const hasType =
    metadata['budovaBuildingType'] != null
    || custom?.['budovaBuildingType'] != null;
  const hasWorkflow =
    metadata['budovaWorkflowId'] != null
    || custom?.['budovaWorkflowId'] != null;
  if (hasType || hasWorkflow) return false;
  const ms = (metadata['budovaMilestones'] ?? custom?.['budovaMilestones']) as
    | Record<string, unknown>
    | undefined;
  if (!ms || typeof ms !== 'object') return false;
  const clearedFlagKeys = ['zavrtany', 'zrizeneVr', 'postavenaBabovka', 'zafouknuto'] as const;
  if (!clearedFlagKeys.every((k) => ms[k] !== true)) return false;
  const completedAt = ms['completedAt'];
  if (completedAt && typeof completedAt === 'object' && !Array.isArray(completedAt)) {
    const extra = Object.entries(completedAt as Record<string, unknown>).filter(([key, v]) => {
      if (String(key).trim().toUpperCase() === 'ODBOCENO') return false;
      return typeof v === 'string' && v.trim().length > 0;
    });
    if (extra.length > 0) return false;
  }
  return true;
}

/**
 * Metadata patch when building type selection is reverted.
 * Clears `budovaWorkflowId` so `inferBudovaBuildingType` returns null (type picker),
 * instead of writing 7 (ZAMITNUTA) from `getInitialPhase('BUDOVA')` / BUDOVA.1.
 * Keeps odbočka progress; reverts VR / závrt / připravená bábovka (and zafouknuto).
 */
export function buildBuildingTypeRevertMetaPatch(
  existingMetadata?: Record<string, unknown> | null,
): Record<string, unknown> {
  const milestones = buildMilestonesPreservingOdbocenoForTypeChange(existingMetadata);
  return {
    budovaBuildingType: null,
    [BUDOVA_BUILDING_TYPE_SET_AT_KEY]: null,
    [BUDOVA_BUILDING_TYPE_SET_BY_KEY]: null,
    budovaWorkflowId: null,
    [BUDOVA_METADATA_KEYS.budovaMilestones]: milestones,
    customFields: {
      budovaBuildingType: null,
      [BUDOVA_BUILDING_TYPE_SET_AT_KEY]: null,
      [BUDOVA_BUILDING_TYPE_SET_BY_KEY]: null,
      budovaWorkflowId: null,
      [BUDOVA_METADATA_KEYS.budovaMilestones]: milestones,
    },
  };
}

/**
 * Apply [buildBuildingTypeRevertMetaPatch] onto local metadata (null → delete key).
 * Use on web/Android local cache after revert so clients do not hand-roll clear maps.
 */
export function applyBuildingTypeRevertMetaPatch(
  metadata: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const patch = buildBuildingTypeRevertMetaPatch(metadata);
  const meta: Record<string, unknown> = { ...(metadata ?? {}) };
  const customPatch = (patch['customFields'] ?? {}) as Record<string, unknown>;
  for (const [k, v] of Object.entries(patch)) {
    if (k === 'customFields') continue;
    if (v === null) delete meta[k];
    else meta[k] = v;
  }
  const custom: Record<string, unknown> = {
    ...((meta['customFields'] as Record<string, unknown> | undefined) ?? {}),
  };
  for (const [k, v] of Object.entries(customPatch)) {
    if (v === null) delete custom[k];
    else custom[k] = v;
  }
  meta['customFields'] = custom;
  return meta;
}
